import { Play, Route, X } from "lucide-react";
import type { WeeklyWalkthrough } from "../../lib/weekly-walkthroughs";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface WeeklyWalkthroughCueProps {
  walkthrough: WeeklyWalkthrough | null;
  onClose: () => void;
  onReplay: () => void;
}

export function WeeklyWalkthroughCue({
  walkthrough,
  onClose,
  onReplay,
}: WeeklyWalkthroughCueProps) {
  if (!walkthrough) return null;

  return (
    <aside
      data-weekly-walkthrough-cue={walkthrough.id}
      className="fixed bottom-4 right-4 z-[270] w-[min(372px,calc(100vw-24px))] rounded-md border border-[color-mix(in_srgb,var(--fintheon-accent)_18%,transparent)] bg-[color-mix(in_srgb,var(--fintheon-bg)_92%,black)] p-4 text-[var(--fintheon-text)] backdrop-blur-md"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Route className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fintheon-accent)]/72" />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fintheon-accent)]/64">
            Weekly walkthrough
          </p>
          <h2 className="mt-1 text-[14px] font-semibold leading-tight">
            {walkthrough.title}
          </h2>
          <p className="mt-2 text-[11px] leading-5 text-[var(--fintheon-text)]/60">
            {walkthrough.outcome}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-[var(--fintheon-text)]/40 transition-colors hover:bg-[var(--fintheon-accent)]/8 hover:text-[var(--fintheon-accent)]"
          aria-label="Close walkthrough"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <ol className="mt-3 space-y-1.5">
        {walkthrough.steps.map((step, index) => (
          <li
            key={step}
            className="grid grid-cols-[18px_1fr] gap-2 text-[11px] leading-5 text-[var(--fintheon-text)]/68"
          >
            <span className="font-mono text-[10px] text-[var(--fintheon-accent)]/72">
              <FintheonNumericText text={index + 1} />
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      <div className="mt-3 border-t border-[color-mix(in_srgb,var(--fintheon-accent)_10%,transparent)] pt-3">
        <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-text)]/34">
          {walkthrough.targetAction}
        </p>
        <button
          type="button"
          onClick={onReplay}
          className="mt-2 inline-flex items-center gap-2 rounded border border-[color-mix(in_srgb,var(--fintheon-accent)_24%,transparent)] px-2.5 py-1.5 text-[11px] font-semibold text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/8"
        >
          <Play className="h-3 w-3" />
          {walkthrough.ctaLabel}
        </button>
      </div>
    </aside>
  );
}
